import React, { createRef, useEffect, useState } from "react";
import axios from "axios";
import { FixedCropper, ImageRestriction } from "react-advanced-cropper";
import { getZoomFactor } from "advanced-cropper/extensions/absolute-zoom";
import "react-advanced-cropper/dist/style.css";
import { checkPhoto } from "../../../../../api/player";
import { uploadPhotoErrors, uploadPhotoRequirements } from "../../core/models";
import * as constants from "../../core/constants";
import { RenderIcon } from "../../components/Icons";
import Preloader from "../../../../../offline/src/components/Preloader";
import UIButton from "../../../../../globalComponents/UIComponents/UIButton";
import { useLocaleContext } from "../../../../../platform/src/context/LocaleContext";

const RANGE_MIN = 0;
const RANGE_MAX = 1;
const RANGE_STEP = 0.02;
const FILE_MAX_SIZE = 20971520;
const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg', 'image/bmp', 'image/heif', 'image/heic'];

const UploadPhotoEditor = ({
    eventKey, photo, initialPhoto, isViewingPhoto, onSetInitialPhoto, onChange, onClose,
}) => {
    const $locale = useLocaleContext();
    const CancelToken = axios.CancelToken;
    const [imageSrc, setImageSrc] = useState(null);
    const [fileType, setFileType] = useState(null);
    const [isCroppMode, setCroppMode] = useState(false);
    const [isPreloader, setPreloader] = useState(false);
    const [isValidPhoto, setValidPhoto] = useState(true);
    const [uploadedFile, setUploadedFile] = useState(initialPhoto || null);
    const [errorMessage, setErrorMessage] = useState('');
    const [zoomValue, setZoomValue] = useState(RANGE_MIN);
    const [source, setSource] = useState(null);

    const cropperRef = createRef();
    const rangeRef = createRef();

    useEffect(() => {
        const source = CancelToken.source();
        setSource(source);
    }, []);

    useEffect(async () => {
        if (!photo) {
            return;
        }

        setImageSrc(URL.createObjectURL(photo));
    }, [photo]);

    const handleUploadPhoto = (file) => {
        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
            setValidPhoto(false);
            setErrorMessage(uploadPhotoErrors[constants.PHOTO_ERROR_FORMAT]);

            return;
        }

        setFileType(file.type);

        if (file.size > FILE_MAX_SIZE) {
            setValidPhoto(false);
            setErrorMessage(uploadPhotoErrors[constants.PHOTO_ERROR_SIZE]);

            return;
        }

        onSetInitialPhoto(file);
        setUploadedFile(file);
        setImageSrc(URL.createObjectURL(file));
        setValidPhoto(true);
        setCroppMode(true);
    };

    const handleCropPhoto = async () => {
        const cropper = cropperRef.current;

        if (!cropper) return;

        const dataURL = cropper.getCanvas().toDataURL(fileType);
        const response = await fetch(dataURL);
        const blob = await response.blob();
        const file = new File(
            [blob],
            "fileName.jpg",
            {
              type: fileType,
              lastModified: new Date(),
            }
        );

        handleCheckPhoto(file);
        setImageSrc(dataURL);
        setCroppMode(false);
    };


    const handleCheckPhoto = async (file) => {
        const timeout = setTimeout(() => {
            source.cancel();
            setPreloader(false);
            setValidPhoto(true);
            onChange(file);
            onClose();
        }, 25000);

        try {
            setPreloader(true);

            const formData = new FormData();
            formData.append('file', file);

            await checkPhoto(eventKey, formData, {cancelToken: source.token});

            setPreloader(false);
            setValidPhoto(true);
            onChange(file);
            onClose();
        } catch (error) {
            console.error(error);
            setValidPhoto(false);
            setErrorMessage(error.data?.message);
        } finally {
            clearTimeout(timeout);
            setPreloader(false);
        }
    };

    const handleBack = () => {
        if (isViewingPhoto) {
            onClose();
            setValidPhoto(true);

            return;
        }

        if (isCroppMode || !isValidPhoto) {
            setCroppMode(false);
            setUploadedFile(null);
            onSetInitialPhoto(null);
            setValidPhoto(true);
            setErrorMessage('');

            return;
        }

        if (uploadedFile) {
            setUploadedFile(null);

            return;
        }

        onClose();
    }

    const handleEditPhoto = () => {
        setCroppMode(true);
        setImageSrc(URL.createObjectURL(uploadedFile));
    };

    const handleClose = () => {
        setCroppMode(false);
        setUploadedFile(null);
        onSetInitialPhoto(null);
        setValidPhoto(true);
        setErrorMessage('');
        onClose();
    }

    const handleChangeRange = (value) => {
        const cropper = cropperRef.current;

        if (!cropper) return;

        const state = cropper.getState();
        const settings = cropper.getSettings();

        cropper.zoomImage(getZoomFactor(state, settings, value), {});

        setZoomValue(value);

        const progress = Math.round((value / RANGE_MAX) * 100);
        rangeRef.current.style.background = `
            linear-gradient(to right, rgb(var(--app-colorAccent)) ${progress}%, rgba(var(--app-titleColor), .4) ${progress}%)
        `;
    };

    return (
        <div className="upload-photo-editor">
            <button
                type="button"
                className="upload-photo-editor__back"
                onClick={handleBack}
            >
                <RenderIcon iconName={constants.ICON_BACK} />
                {$locale.get('registration.addPhoto.back')}
            </button>
            {!uploadedFile && (
                <>
                    <div>
                        <div className="upload-photo-editor__example-photo">
                            <img
                                src="/player/images/example-photo.jpg"
                                alt={$locale.get('registration.addPhoto.examplePhoto')}
                            />
                        </div>
                        <p className="upload-photo-editor__example-text">
                            {$locale.get('registration.addPhoto.examplePhoto')}
                        </p>
                    </div>
                    <div>
                        <p className="upload-photo-editor__title-list">
                            {$locale.get('registration.addPhoto.requirements')}
                        </p>
                        <ul className="upload-photo-editor__list">
                            {Object.entries(uploadPhotoRequirements).map(([key, value]) => (
                                <li
                                    key={key}
                                    className="upload-photo-editor__list-item"
                                >
                                    <RenderIcon iconName={constants.ICON_SUCCESS} />
                                    {$locale.get(`${value}`)};
                                </li>
                            ))}
                        </ul>
                        {!isValidPhoto && (
                            <p className="upload-photo-editor__error-message">
                                * {$locale.get(errorMessage)}
                            </p>
                        )}
                    </div>
                    <UIButton
                        color="primary"
                        size="large"
                        style="upload-photo-button file-button"
                    >
                        {$locale.get('registration.addPhoto.upload')}
                        <input
                            type="file"
                            onChange={(e) => handleUploadPhoto(e.target.files[0])}
                        />
                    </UIButton>
                </> 
            )}
            {uploadedFile && (
                <>
                    {isCroppMode
                        ? <>
                            <div className="upload-photo-editor__container-cropper">
                                <FixedCropper
                                    ref={cropperRef}
                                    src={imageSrc}
                                    stencilSize={{
                                        width: 300,
                                        height: 400,
                                    }}
                                    stencilProps={{
                                        movable: false,
                                        resizable: false,
                                        handlers: {},
                                        lines: {},
                                    }}
                                    imageRestriction={ImageRestriction.stencil}
                                    backgroundWrapperProps={{
                                        scaleImage: false,
                                    }}
                                />
                            </div>
                            <div className="upload-photo-editor__range-container">
                                <span
                                    className="upload-photo-editor__range-buttons"
                                    onClick={() => handleChangeRange(+zoomValue - RANGE_STEP)}
                                >
                                    <RenderIcon iconName={constants.ICON_MINUS} />
                                </span>
                                <input
                                    ref={rangeRef}
                                    type="range"
                                    value={zoomValue}
                                    step={RANGE_STEP}
                                    min={RANGE_MIN}
                                    max={RANGE_MAX}
                                    className="upload-photo-editor__range"
                                    onChange={(e) => handleChangeRange(e.target.value)}
                                />
                                <span
                                    className="upload-photo-editor__range-buttons"
                                    onClick={() => handleChangeRange(+zoomValue + RANGE_STEP)}
                                >
                                    <RenderIcon iconName={constants.ICON_PLUS} />
                                </span>
                            </div>
                            {!isValidPhoto && (
                                <>
                                    <p className="upload-photo-editor__error-message">
                                        * {$locale.get('registration.addPhoto.uploadFail')}
                                    </p>
                                </>
                            )}
                        </>
                        : <div className="upload-photo-editor__block">
                            <div className={`upload-photo-editor__container-image ${!isValidPhoto ? 'blur' : ''}`}>
                                <img
                                    src={imageSrc}
                                    alt=""
                                />
                            </div>
                            {!isValidPhoto && (
                                <>
                                    <RenderIcon iconName={constants.ICON_WARNING} />
                                    <p className="upload-photo-editor__varification-text">
                                        {$locale.get('registration.addPhoto.verificationFail')}
                                    </p>
                                    {errorMessage && (
                                        <p className="upload-photo-editor__error-message transformed">{errorMessage}</p>
                                    )}
                                </>
                            )}
                        </div>
                    }
                    <div className="upload-photo-editor__footer">
                        {isValidPhoto
                            ? <>
                                <UIButton
                                    color="secondary"
                                    style="upload-photo-button file-button"
                                >
                                    <RenderIcon iconName={constants.ICON_ADD} />
                                    {$locale.get('registration.addPhoto.chooseAnother')}
                                    <input
                                        type="file"
                                        onChange={(e) => handleUploadPhoto(e.target.files[0])}
                                    />
                                </UIButton>
                                {isViewingPhoto && !isCroppMode
                                    ? <UIButton
                                        style="upload-photo-button"
                                        onClick={handleEditPhoto}
                                    >
                                        <RenderIcon iconName={constants.ICON_EDIT} />
                                        {$locale.get('registration.addPhoto.edit')}
                                    </UIButton>
                                    : <UIButton
                                        color="primary"
                                        style="upload-photo-button"
                                        onClick={handleCropPhoto}
                                    >
                                        {$locale.get('registration.addPhoto.save')}
                                    </UIButton>
                                }
                            </>
                            : <>
                                <UIButton
                                    style="upload-photo-button"
                                    onClick={handleClose}
                                >
                                    <RenderIcon iconName={constants.ICON_CLOSE} />
                                    {$locale.get('registration.addPhoto.exit')}
                                </UIButton>
                                <UIButton
                                    color="primary"
                                    style="upload-photo-button file-button"
                                >
                                    {$locale.get('registration.addPhoto.uploadNewPhoto')}
                                    <input
                                        type="file"
                                        onChange={(e) => handleUploadPhoto(e.target.files[0])}
                                    />
                                </UIButton>
                            </>
                        }
                    </div>
                </>
            )}
            {isPreloader && (
                <div className="upload-photo-editor__preloader">
                    <Preloader />
                </div>
            )}
        </div>
    );
};

export default UploadPhotoEditor;
